import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { INTEREST_LABELS, LOGO_URL } from "@/lib/gateways";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "selahstone_admin_token";

const INTEREST_FILTERS = [
  { id: "all", label: "All" },
  { id: "selah-and-stone-research", label: "Research" },
  { id: "selah-and-salt-home", label: "Salt Home" },
  { id: "family-legacy-audits", label: "Family Audits" },
  { id: "selah-shift-book", label: "The Selah Shift" },
  { id: "general", label: "General" },
  { id: "selah-slate", label: "Slate (legacy)" },
  { id: "selah-downloads", label: "Downloads (legacy)" },
];

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso || "";
  }
};

const LoginScreen = ({ onAuthed }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      if (data?.ok) {
        sessionStorage.setItem(TOKEN_KEY, data.token);
        onAuthed();
      } else {
        toast.error("Invalid credentials.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="paper-grain relative min-h-screen flex items-center justify-center px-6"
      data-testid="admin-login"
    >
      <div className="relative z-10 w-full max-w-sm">
        <div className="text-center mb-10">
          <img
            src={LOGO_URL}
            alt="Selah and Stone Legacy Holdings"
            className="mx-auto h-20 w-20"
          />
          <p
            className="mt-6 font-sans-body text-[10px] tracking-[0.36em] uppercase"
            style={{ color: "var(--olive)" }}
          >
            Private Threshold
          </p>
          <h1
            className="mt-3 font-serif-display text-4xl font-light"
            style={{ color: "var(--ink)" }}
          >
            Admin
          </h1>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-1.5">
            <label
              htmlFor="pw"
              className="font-sans-body text-[11px] tracking-[0.22em] uppercase block"
              style={{ color: "var(--stone-deep)" }}
            >
              Password
            </label>
            <input
              id="pw"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="w-full rounded-none border-0 border-b border-[rgba(176,133,94,0.6)] bg-transparent px-0 py-2 font-serif-display text-xl focus:outline-none focus:border-[var(--olive)]"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="btn-olive w-full px-6 py-3 text-[11px] uppercase font-sans-body disabled:opacity-60"
          >
            {loading ? "Verifying..." : "Enter"}
          </button>
        </form>
        <p
          className="mt-8 text-center font-sans-body text-[11px]"
          style={{ color: "var(--stone-deep)" }}
        >
          <a href="/" className="underline hover:no-underline">
            Back to the site
          </a>
        </p>
      </div>
    </div>
  );
};

const AdminDashboard = ({ onLogout }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);

  const token = sessionStorage.getItem(TOKEN_KEY);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.interest = filter;
      if (query.trim()) params.q = query.trim();
      const { data } = await axios.get(`${API}/admin/waitlist`, {
        headers: { "X-Admin-Password": token },
        params,
      });
      setEntries(data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        onLogout();
      } else {
        toast.error("Couldn't load entries.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const totals = useMemo(() => {
    const byInterest = {};
    entries.forEach((e) => {
      byInterest[e.interest] = (byInterest[e.interest] || 0) + 1;
    });
    return { total: entries.length, byInterest };
  }, [entries]);

  const downloadCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist.csv`, {
        headers: { "X-Admin-Password": token },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `selah-waitlist-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("CSV download failed.");
    }
  };

  const resendAlert = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/waitlist/${id}/resend-alert`, null, {
        headers: { "X-Admin-Password": token },
      });
      toast.success("Alert re-sent.");
    } catch {
      toast.error("Resend failed.");
    } finally {
      setBusyId(null);
    }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/waitlist/${id}`, {
        headers: { "X-Admin-Password": token },
      });
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Entry deleted.");
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="paper-grain relative min-h-screen" data-testid="admin-dashboard">
      <div className="relative z-10">
        {/* Header */}
        <header className="px-6 sm:px-10 pt-8 pb-8 border-b border-[rgba(176,133,94,0.35)]">
          <div className="mx-auto max-w-6xl flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p
                className="font-sans-body text-[10px] tracking-[0.36em] uppercase"
                style={{ color: "var(--olive)" }}
              >
                Private Threshold
              </p>
              <h1
                className="mt-2 font-serif-display text-4xl sm:text-5xl font-light"
                style={{ color: "var(--ink)" }}
              >
                Waitlist
              </h1>
              <p
                className="mt-2 font-sans-body text-sm"
                style={{ color: "var(--stone-deep)" }}
              >
                {totals.total} {totals.total === 1 ? "entry" : "entries"}
                {filter !== "all" ? ` in ${INTEREST_LABELS[filter] || filter}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={downloadCSV}
                data-testid="admin-csv-button"
                className="btn-ghost-olive px-4 py-2 text-[11px] uppercase font-sans-body"
              >
                Export CSV
              </button>
              <button
                onClick={onLogout}
                data-testid="admin-logout-button"
                className="font-sans-body text-[11px] tracking-[0.22em] uppercase px-3 py-2"
                style={{ color: "var(--stone-deep)" }}
              >
                Log out
              </button>
            </div>
          </div>
        </header>

        {/* Filters + search */}
        <section className="px-6 sm:px-10 pt-8">
          <div className="mx-auto max-w-6xl flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2" data-testid="admin-filters">
              {INTEREST_FILTERS.map((f) => {
                const active = filter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className="px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-sans-body transition-all"
                    style={{
                      border: "1px solid rgba(63,84,104,0.35)",
                      background: active ? "var(--olive)" : "transparent",
                      color: active ? "var(--parchment)" : "var(--olive)",
                    }}
                    data-testid={`admin-filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                fetchEntries();
              }}
              className="flex items-center gap-2 w-full lg:w-auto"
            >
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, or note"
                data-testid="admin-search-input"
                className="flex-1 lg:w-72 rounded-none border-0 border-b border-[rgba(176,133,94,0.6)] bg-transparent px-0 py-2 font-sans-body text-sm focus:outline-none focus:border-[var(--olive)]"
              />
              <button
                type="submit"
                className="btn-ghost-olive px-4 py-2 text-[11px] uppercase font-sans-body"
                data-testid="admin-search-button"
              >
                Search
              </button>
            </form>
          </div>
        </section>

        {/* Table */}
        <section className="px-6 sm:px-10 py-10">
          <div className="mx-auto max-w-6xl">
            {loading ? (
              <p
                className="font-serif-display italic text-lg"
                style={{ color: "var(--stone-deep)" }}
              >
                Loading…
              </p>
            ) : entries.length === 0 ? (
              <div
                className="py-16 text-center border border-[rgba(176,133,94,0.35)]"
                style={{ background: "rgba(255,255,255,0.35)" }}
              >
                <p
                  className="font-serif-display italic text-xl"
                  style={{ color: "var(--stone-deep)" }}
                >
                  No entries to show.
                </p>
              </div>
            ) : (
              <div
                className="overflow-x-auto border border-[rgba(176,133,94,0.35)]"
                style={{ background: "rgba(255,255,255,0.5)" }}
              >
                <table
                  className="w-full font-sans-body text-sm"
                  data-testid="admin-table"
                >
                  <thead>
                    <tr
                      className="text-left text-[10px] tracking-[0.22em] uppercase"
                      style={{ color: "var(--stone-deep)" }}
                    >
                      <th className="px-5 py-3 border-b border-[rgba(176,133,94,0.35)]">
                        Submitted
                      </th>
                      <th className="px-5 py-3 border-b border-[rgba(176,133,94,0.35)]">
                        Name
                      </th>
                      <th className="px-5 py-3 border-b border-[rgba(176,133,94,0.35)]">
                        Email
                      </th>
                      <th className="px-5 py-3 border-b border-[rgba(176,133,94,0.35)]">
                        Interest
                      </th>
                      <th className="px-5 py-3 border-b border-[rgba(176,133,94,0.35)]">
                        Note
                      </th>
                      <th className="px-5 py-3 border-b border-[rgba(176,133,94,0.35)] text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr
                        key={e.id}
                        className="align-top border-b border-[rgba(176,133,94,0.18)] hover:bg-[rgba(255,255,255,0.35)] transition-colors"
                        data-testid={`admin-row-${e.id}`}
                      >
                        <td
                          className="px-5 py-4 whitespace-nowrap font-mono text-[11px]"
                          style={{ color: "var(--stone-deep)" }}
                        >
                          {formatDate(e.created_at)}
                        </td>
                        <td
                          className="px-5 py-4 font-serif-display text-base"
                          style={{ color: "var(--ink)" }}
                        >
                          {e.name}
                        </td>
                        <td className="px-5 py-4">
                          <a
                            href={`mailto:${e.email}`}
                            className="underline decoration-dotted"
                            style={{ color: "var(--olive)" }}
                          >
                            {e.email}
                          </a>
                        </td>
                        <td
                          className="px-5 py-4 italic"
                          style={{ color: "var(--stone-deep)" }}
                        >
                          {INTEREST_LABELS[e.interest] || e.interest}
                        </td>
                        <td
                          className="px-5 py-4 max-w-[260px]"
                          style={{ color: "var(--ink)" }}
                        >
                          {e.message ? (
                            <span className="text-[13px]">{e.message}</span>
                          ) : (
                            <span style={{ color: "var(--stone)" }}>—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right whitespace-nowrap">
                          <button
                            onClick={() => resendAlert(e.id)}
                            disabled={busyId === e.id}
                            className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 mr-2 disabled:opacity-50"
                            style={{ color: "var(--olive)" }}
                            data-testid={`admin-resend-${e.id}`}
                          >
                            Resend
                          </button>
                          <button
                            onClick={() => removeEntry(e.id)}
                            disabled={busyId === e.id}
                            className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 disabled:opacity-50"
                            style={{ color: "#8a2f2f" }}
                            data-testid={`admin-delete-${e.id}`}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const Admin = () => {
  useSEO({
    title: "Admin — Selah & Stone Legacy Holdings",
    description: "Private administrative area.",
    path: "/admin",
    noindex: true,
  });

  const [authed, setAuthed] = useState(
    Boolean(sessionStorage.getItem(TOKEN_KEY))
  );

  if (!authed) {
    return <LoginScreen onAuthed={() => setAuthed(true)} />;
  }
  return (
    <AdminDashboard
      onLogout={() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      }}
    />
  );
};

export default Admin;
